import { BonusType } from './bonuses';
import { BET_BUILDER_MIN_SELECTIONS } from './bet-builder';
import {
  countBets,
  getBetOddFactor,
  type BetslipBet,
  type BetslipTicket,
} from './betslip';
import { appliedRewardFromTicket } from './rewards';

/** Stake / odds limits used at place time (subset of account/user BetSettings). */
export interface PlaceBetLimits {
  MinimumBet?: number;
  MinimumBetLive?: number;
  MaximumBet?: number;
  MaximumBetLive?: number;
  MaximumOdds?: number;
  MaximumOddsLive?: number;
  MaximumWin?: number;
  MaximumWinLive?: number;
  MaximumCombi?: number;
  MaximumCombiLive?: number;
}

/** Locked event returned when the API rejects a leg (typed for later UX). */
export interface PlaceBetLockedEvent {
  EventId: string | number;
  Limit?: number;
  ProposedOdd?: number;
}

/** Response from `sb/api/tickets/create`. */
export interface PlaceBetResponse {
  Success: boolean;
  TicketNumber?: string | number;
  Error?: string;
  Limit?: number;
  LockedEvents?: PlaceBetLockedEvent[] | null;
}

/** Session / brand fields attached at submit time. */
export interface PlaceBetContext {
  accountGuid: string;
  currencyCode: string;
  language: string;
  /**
   * Player machine GUID (`LoginResponse.Guid` / `Machines.MachineGuid`).
   * UsersAPI looks up the player by this value in `TicketRequest.User`.
   */
  userGuid: string;
  options?: string;
  isMobile: boolean;
  externalSportRisk?: unknown;
  uApiBase?: string;
  /** Encrypted account-bonus id when an attractive combi tier applies. */
  attractiveCombiId?: string;
}

/**
 * Wire payload for ticket create — betslip ticket plus place-time enrichments.
 * Kept as a plain object so the users-host form encoder can serialize it.
 */
export interface PlaceBetRequest extends BetslipTicket {
  AccountGuid: string;
  CurrencyCode: string;
  Language: string;
  User: string;
  Options?: string;
  IsBonus: boolean;
  StakeBonus: number;
  StakeNormal?: number;
  IsMobile: boolean;
  ExternalSportRisk?: unknown;
  uAPI?: string;
  /** Set by client for KreditBet; server resolves CreditorId. */
  CreditorNumber?: string;
  CreditorId?: number | null;
  /** UsersAPI selects this attractive-combi account bonus when set. */
  AttractiveCombiId?: string;
}

export type PlaceBetMessageLimitKind = 'amount' | 'odds' | 'count';

/** Structured message for validation or API errors (UI translates + formats). */
export interface PlaceBetMessage {
  key: string;
  defaultValue: string;
  limit?: number;
  limitKind?: PlaceBetMessageLimitKind;
}

export type PlaceBetValidationResult =
  | { ok: true }
  | { ok: false; message: PlaceBetMessage };

/** Optional extras for rules that need more than BetSettings. */
export interface PlaceBetValidationExtras {
  /** Player restricted credits balance. */
  restrictedCredits?: number;
  /** Withdrawable / main credits (for restricted-only wallet check). */
  withdrawableCredits?: number;
  /** Bonus credits — when > 0, restricted-min-odds rule does not apply. */
  bonusCredits?: number;
  /** Account.RestrictedMinimumQuota when betting on restricted balance only. */
  restrictedMinimumQuota?: number;
}

function isLiveTicket(ticket: BetslipTicket): boolean {
  return ticket.Live === true;
}

/**
 * Client-side place-bet gates (legacy `CheckUserSettings`, core rules only).
 * Prematch max win uses `MaximumWin` (legacy incorrectly used `MaximumWinLive` for both).
 */
export function validatePlaceBet(
  ticket: BetslipTicket,
  betSettings: PlaceBetLimits | null | undefined,
  extras: PlaceBetValidationExtras = {},
): PlaceBetValidationResult {
  if (countBets(ticket) === 0) {
    return {
      ok: false,
      message: {
        key: 'EmptyBetslipMessage',
        defaultValue: "You haven't selected any bets yet. Tap odds to add selections.",
      },
    };
  }

  for (const item of ticket.Items) {
    for (const bet of item.Bets) {
      if (!bet.Custom) continue;
      const legs = bet.Selections?.length ?? 0;
      if (legs < BET_BUILDER_MIN_SELECTIONS) {
        return {
          ok: false,
          message: {
            key: 'CustomBetMinSelections',
            defaultValue: 'Add at least 2 selections to your Bet Builder',
          },
        };
      }
      if (getBetOddFactor(bet) <= 0 || bet.Odd == null || bet.Odd === '') {
        return {
          ok: false,
          message: {
            key: 'CustomBetPricing',
            defaultValue: 'Calculating Bet Builder odds…',
          },
        };
      }
    }
  }

  const live = isLiveTicket(ticket);
  const settings = betSettings ?? {};
  const settingsMaxStake = live ? settings.MaximumBetLive : settings.MaximumBet;
  const kreditMax =
    ticket.CreditorNumber &&
    typeof ticket.CreditorPossibleWin === 'number' &&
    Number.isFinite(ticket.CreditorPossibleWin)
      ? ticket.CreditorPossibleWin
      : null;
  // Legacy CheckUserSettings: KreditBet overrides max stake with creditor possible win.
  const maximumStake = kreditMax != null ? kreditMax : settingsMaxStake;
  const minimumStake = live ? settings.MinimumBetLive : settings.MinimumBet;
  const maximumOdds = live ? settings.MaximumOddsLive : settings.MaximumOdds;
  const maximumWin = live ? settings.MaximumWinLive : settings.MaximumWin;
  const maximumCombi = live ? settings.MaximumCombiLive : settings.MaximumCombi;

  const stake = ticket.Stake ?? 0;
  if (stake <= 0) {
    return {
      ok: false,
      message: {
        key: 'NotEnoughCredits',
        defaultValue: 'Not enough credits',
      },
    };
  }

  if (maximumStake != null && stake > maximumStake) {
    return {
      ok: false,
      message: {
        key: kreditMax != null ? 'InvalidStake' : 'MaximumTicketStake',
        defaultValue: kreditMax != null ? 'Stake exceeds max Kredit' : 'Maximum stake is',
        limit: maximumStake,
        limitKind: 'amount',
      },
    };
  }

  if (minimumStake != null && stake < minimumStake) {
    return {
      ok: false,
      message: {
        key: 'MinimumTicketStake',
        defaultValue: 'Minimum stake is',
        limit: minimumStake,
        limitKind: 'amount',
      },
    };
  }

  const odds = ticket.Odds ?? 0;
  if (maximumOdds != null && odds > maximumOdds) {
    return {
      ok: false,
      message: {
        key: 'MaximumTicketOdds',
        defaultValue: 'Maximum odds are',
        limit: maximumOdds,
        limitKind: 'odds',
      },
    };
  }

  const restrictedOnly =
    (extras.restrictedCredits ?? 0) > 0 &&
    (extras.bonusCredits ?? 0) === 0 &&
    (extras.withdrawableCredits ?? 0) === 0;
  if (
    restrictedOnly &&
    extras.restrictedMinimumQuota != null &&
    odds < extras.restrictedMinimumQuota
  ) {
    return {
      ok: false,
      message: {
        key: 'RestrictedMinimumOdds',
        defaultValue: 'Minimum odds for restricted credits are',
        limit: extras.restrictedMinimumQuota,
        limitKind: 'odds',
      },
    };
  }

  const possibleWin = ticket.PossibleWin ?? 0;
  if (maximumWin != null && possibleWin > maximumWin) {
    return {
      ok: false,
      message: {
        key: 'MaximumTicketWin',
        defaultValue: 'Maximum win is',
        limit: maximumWin,
        limitKind: 'amount',
      },
    };
  }

  const rows = ticket.Rows ?? 0;
  if (maximumCombi != null && rows > maximumCombi) {
    return {
      ok: false,
      message: {
        key: 'MaximumBetsCombi',
        defaultValue: 'Maximum combinations are',
        limit: maximumCombi,
        limitKind: 'count',
      },
    };
  }

  return { ok: true };
}

function stringifySpecifierDescription(bet: BetslipBet): BetslipBet {
  let next = bet;
  if (bet.SpecifierDescription != null && typeof bet.SpecifierDescription !== 'string') {
    next = {
      ...next,
      SpecifierDescription: JSON.stringify(bet.SpecifierDescription),
    };
  }
  if (next.Custom && next.Selections?.length) {
    next = {
      ...next,
      Selections: next.Selections.map((sel) => {
        if (sel.SpecifierDescription == null || typeof sel.SpecifierDescription === 'string') {
          return sel;
        }
        return {
          ...sel,
          SpecifierDescription: JSON.stringify(sel.SpecifierDescription),
        };
      }),
    };
  }
  return next;
}

/**
 * Clone the slip and attach place-time fields. Does not mutate the store ticket.
 */
export function buildPlaceBetRequest(
  ticket: BetslipTicket,
  context: PlaceBetContext,
): PlaceBetRequest {
  const cloned = structuredClone(ticket);
  cloned.Items = cloned.Items.map((item) => ({
    ...item,
    Bets: item.Bets.map(stringifySpecifierDescription),
  }));

  // FreeBet: stake is paid by the reward — do not debit player credits.
  const appliedReward = appliedRewardFromTicket(cloned);
  const stakeNormal =
    appliedReward?.Type === BonusType.FreeBet ? 0 : cloned.Stake;

  const request: PlaceBetRequest = {
    ...cloned,
    AccountGuid: context.accountGuid,
    CurrencyCode: context.currencyCode,
    Language: context.language,
    User: context.userGuid,
    IsBonus: false,
    StakeBonus: 0,
    StakeNormal: stakeNormal,
    IsMobile: context.isMobile,
  };

  if (context.options != null) request.Options = context.options;
  if (context.attractiveCombiId) request.AttractiveCombiId = context.attractiveCombiId;
  if (context.externalSportRisk !== undefined) {
    request.ExternalSportRisk = context.externalSportRisk;
  }
  if (context.uApiBase) request.uAPI = context.uApiBase;

  return request;
}

/** Thrown (or set as mutation error) when client validation or API rejects a place. */
export class PlaceBetFailure extends Error {
  readonly placeBetMessage: PlaceBetMessage;

  constructor(message: PlaceBetMessage) {
    super(message.defaultValue);
    this.name = 'PlaceBetFailure';
    this.placeBetMessage = message;
  }
}

/**
 * Map API error codes to translation keys (legacy `ParseError`, core codes).
 */
export function mapPlaceBetError(response: PlaceBetResponse): PlaceBetMessage {
  const code = response.Error ?? '';
  switch (code) {
    case 'MaximumStake':
      return {
        key: 'MaximumTicketStake',
        defaultValue: 'Maximum stake is',
        limit: response.Limit,
        limitKind: 'amount',
      };
    case 'MinimumStake':
      return {
        key: 'MinimumTicketStake',
        defaultValue: 'Minimum stake is',
        limit: response.Limit,
        limitKind: 'amount',
      };
    case 'MaximumOdds':
      return {
        key: 'MaximumTicketOdds',
        defaultValue: 'Maximum odds are',
        limit: response.Limit,
        limitKind: 'odds',
      };
    case 'MaximumWin':
      return {
        key: 'MaximumTicketWin',
        defaultValue: 'Maximum win is',
        limit: response.Limit,
        limitKind: 'amount',
      };
    case 'RiskLimit':
      return {
        key: 'MaximumEventStake',
        defaultValue: 'Maximum stake for an event was exceeded',
        limit: response.LockedEvents?.[0]?.Limit,
        limitKind: 'amount',
      };
    case 'CustomBetOddsChanged':
      return {
        key: 'CustomBetOddsChanged',
        defaultValue: 'Bet Builder odds have changed',
        limit: response.Limit,
        limitKind: 'odds',
      };
    case 'CustomBetCannotCombine':
      return {
        key: 'CustomBetCannotCombine',
        defaultValue: 'These selections cannot be combined',
      };
    case 'CustomBetMixedWithSingle':
      return {
        key: 'CustomBetMixedWithSingle',
        defaultValue: 'Cannot mix Bet Builder with a standard bet on the same event',
      };
    case 'InvalidCreditor':
      return {
        key: 'InvalidCreditor',
        defaultValue: 'Selected ticket is not valid for KreditBet',
      };
    case 'InvalidStake':
      return {
        key: 'InvalidStake',
        defaultValue: 'Stake exceeds max Kredit',
        limit: response.Limit,
        limitKind: 'amount',
      };
    case 'EventsOverlapping':
      return {
        key: 'EventsOverlapping',
        defaultValue: 'KreditBet cannot share events with the selected ticket',
      };
    case 'InvalidRequest':
      return {
        key: 'InvalidRequest',
        defaultValue: 'Unable to place KreditBet with the current slip',
      };
    default:
      return {
        key: code || 'Error',
        defaultValue: code || 'Unable to place bet',
      };
  }
}
